/**
 * Empreinte de fraîcheur — EF-CAL-005.
 *
 * Un résultat de calcul porte une empreinte des données dont il dépend : le
 * parcours, l'ensemble des POI et leurs passages, la curation et les paramètres
 * de calcul. À chaque affichage, l'application compare cette empreinte à l'état
 * courant ; toute divergence lève le marqueur « valeurs périmées ».
 *
 * La canonicité est obligatoire — clés triées, nombres formatés de façon
 * déterministe — sans quoi deux états identiques produiraient des empreintes
 * différentes et le marqueur s'afficherait à tort.
 *
 * Le corridor de recherche de EF-POI-002 ne figure **pas** dans l'empreinte, et
 * c'est délibéré : la portée de passage étant attachée au POI, modifier le
 * corridor ne change ni les passages, ni les lignes, ni aucune valeur calculée.
 */

import type { Poi, PoiId } from '../core/model';
import type { ComputationParameters } from '../core/parameters';
import type { ComputedTrack } from '../geo/track';

const FRESHNESS_PREFIX = 'rb1:';

/** Formatage déterministe d'un nombre, indépendant de la locale. */
function canonicalNumber(value: number): string {
  if (Number.isNaN(value)) return 'NaN';
  if (!Number.isFinite(value)) return value > 0 ? 'Inf' : '-Inf';
  if (Number.isInteger(value)) return value.toFixed(0);
  return value.toPrecision(15);
}

function canonicalBoolean(value: boolean): string {
  return value ? '1' : '0';
}

function canonicalString(value: string | null): string {
  return value === null ? ' ' : value;
}

/**
 * Empreinte du parcours, calculée une fois à l'importation et conservée avec
 * lui : la trace ne change jamais au cours de la vie d'un document.
 */
export function trackFingerprint(track: ComputedTrack): string {
  const parts: string[] = [
    canonicalNumber(track.count),
    canonicalNumber(track.lengthMeters),
    canonicalNumber(track.elevationGainMeters ?? Number.NaN),
    canonicalBoolean(track.elevation.available),
  ];
  // Échantillonnage régulier des coordonnées : parcourir quinze mille points à
  // chaque affichage coûterait plus que l'empreinte ne rapporte, et deux traces
  // distinctes ne peuvent coïncider sur ces trois séries à la fois.
  const stride = Math.max(1, Math.floor(track.count / 512));
  for (let i = 0; i < track.count; i += stride) {
    parts.push(canonicalNumber(track.lat[i] ?? Number.NaN));
    parts.push(canonicalNumber(track.lon[i] ?? Number.NaN));
    parts.push(canonicalNumber(track.alt[i] ?? Number.NaN));
  }
  return hash(parts.join('|'));
}

export interface FreshnessInput {
  readonly routeFingerprint: string;
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly parameters: ComputationParameters;
}

/** Sérialisation canonique du sous-ensemble contributif. */
export function canonicalFreshnessPayload(input: FreshnessInput): string {
  const parts: string[] = ['route', input.routeFingerprint];

  const ids = [...input.pois.keys()].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  parts.push('pois', canonicalNumber(ids.length));
  for (const id of ids) {
    const poi = input.pois.get(id);
    if (poi === undefined) continue;
    parts.push(
      id,
      poi.origin,
      [...poi.categories].join(','),
      canonicalString(poi.name),
      canonicalNumber(poi.coordinate.lat),
      canonicalNumber(poi.coordinate.lon),
      canonicalNumber(poi.passageReachMeters),
      // La curation participe à l'empreinte : elle modifie les valeurs
      // « depuis le POI précédent » calculées entre POI non exclus.
      canonicalBoolean(poi.userState.excluded),
      canonicalBoolean(poi.userState.pinned),
      canonicalString(poi.userState.note),
    );
    for (const passage of poi.passages) {
      parts.push(
        canonicalNumber(passage.index),
        canonicalNumber(passage.progressionMeters),
        canonicalNumber(passage.distanceToRouteMeters),
      );
    }
  }

  const { speed, schedule } = input.parameters;
  parts.push(
    'speed',
    canonicalNumber(speed.referenceKmh),
    canonicalNumber(speed.uphill5Kmh),
    canonicalNumber(speed.maxKmh),
    canonicalNumber(speed.minKmh),
    'schedule',
    canonicalNumber(schedule.startLocal),
    canonicalBoolean(schedule.pauses.enabled),
    canonicalNumber(schedule.pauses.intervalSeconds),
    canonicalNumber(schedule.pauses.durationSeconds),
    canonicalBoolean(schedule.sleep.enabled),
    canonicalNumber(schedule.sleep.bedTimeSecondsOfDay),
    canonicalNumber(schedule.sleep.durationSeconds),
    canonicalBoolean(schedule.sleep.declinedByUser),
  );

  return parts.join('|');
}

export function computeFreshness(input: FreshnessInput): string {
  return `${FRESHNESS_PREFIX}${hash(canonicalFreshnessPayload(input))}`;
}

/**
 * FNV-1a sur 32 bits, dédoublé avec deux graines distinctes pour produire
 * seize caractères hexadécimaux. L'empreinte est opaque et comparée par
 * égalité stricte, jamais interprétée.
 */
export function hash(text: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    a ^= code;
    a = Math.imul(a, 0x01000193) >>> 0;
    b ^= code + i;
    b = Math.imul(b, 0x85ebca6b) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

/** Vrai lorsque le résultat enregistré ne correspond plus à l'état courant. */
export function isStale(recorded: string | null, current: string): boolean {
  return recorded !== null && recorded !== current;
}
